import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Button, Tab, Tabs, Typography } from '@mui/material';
import { clsx } from 'clsx';
import { differenceInDays, startOfDay } from 'date-fns';
import { setNestedObjectValues, useFormikContext } from 'formik';
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { v4 as uuidv4 } from 'uuid';

import { subroundInitialValue } from '~/pages/CreateCompany';
import { useGetSecurityTypesQuery } from '~/services/rtkQuery/company';
import { CreateCompanyFormValues } from '~/types/formValues';
import { sleep } from '~/utils/sleep';

import { DraggableTab } from './components/DraggableTab';
import { Subround } from './components/Subround';
import { Summary } from './components/Summary';
import styles from './styles.module.scss';

const DRAGGABLE_ROUND = 'ROUND';

type RoundData = CreateCompanyFormValues['form']['rounds'][number];
type SubroundData = RoundData['subrounds'][number];

interface Props {
  round: RoundData;
  roundIndex: number;
  previousRound: RoundData | null;
  swapping: boolean;
  companyLocked: boolean;
  className?: string;
}

export interface RoundRef {
  expand: (subroundIndex: number) => void;
}

const Component = forwardRef<RoundRef, Props>(
  ({ round, roundIndex, previousRound, swapping, companyLocked, className }, ref) => {
    const formik = useFormikContext<CreateCompanyFormValues>();
    const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
    const [expanded, setExpanded] = useState(false);
    const titleRef = useRef<HTMLDivElement | null>(null);
    const securityTypesQuery = useGetSecurityTypesQuery();
    const [{ handlerId }, drop] = useDrop<
      { index: number; data: RoundData },
      void,
      { handlerId: string | symbol | null }
    >({
      accept: DRAGGABLE_ROUND,
      collect(monitor) {
        return {
          handlerId: monitor.getHandlerId(),
        };
      },
      drop(item) {
        const dragIndex = item.index;
        const dragDataSubrounds = [...item.data.subrounds];
        const hoverIndex = roundIndex;
        const hoverDataSubrounds = [...round.subrounds];

        // Don't replace items with themselves
        if (dragIndex === hoverIndex) {
          return;
        }

        (async () => {
          await formik.setFieldValue(`form.rounds.${dragIndex}.subrounds`, hoverDataSubrounds);
          await formik.setFieldValue(`form.rounds.${hoverIndex}.subrounds`, dragDataSubrounds);

          await formik.setTouched(setNestedObjectValues(formik.values, true));
        })();
      },
    });

    const [{ isDragging }, drag] = useDrag({
      type: DRAGGABLE_ROUND,
      item: () => {
        return { index: roundIndex, data: round };
      },
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    });

    useImperativeHandle(
      ref,
      () => ({
        expand: subroundIndex => {
          setExpanded(true);

          setActiveTabIndex(subroundIndex);
        },
      }),
      [],
    );

    useEffect(() => {
      if (roundIndex === 2) {
        const subrounds = formik.values.form.rounds[roundIndex].subrounds;

        subrounds.forEach((subround, index) => {
          if (subround.securityType?.value === 2 || subround.securityType?.value === 3) {
            const nextSubrounds = subrounds.slice(index + 1);
            const nextEquitySubround = nextSubrounds.find(
              subround => subround.securityType?.value === 1,
            );
            const nextSubround = nextEquitySubround || nextSubrounds[nextSubrounds.length - 1];
            const lastSubround = index === subrounds.length - 1;

            if (lastSubround && subround.dealSize && subround.cap) {
              const acquiredCalc =
                parseFloat(subround.dealSize) /
                (parseFloat(subround.cap) + parseFloat(subround.dealSize));

              const safeAcquiredCalc = Number.isFinite(acquiredCalc)
                ? acquiredCalc.toString()
                : '0';

              if (safeAcquiredCalc !== subround.acquiredCalc) {
                formik.setFieldValue(
                  `form.rounds.${roundIndex}.subrounds.${index}.acquiredCalc`,
                  safeAcquiredCalc,
                );
              }
            } else if (
              subround.dealSize &&
              subround.cap &&
              subround.discount &&
              nextSubround?.preMoneyValuation &&
              (subround.securityType?.value !== 3 || subround.conversionAmount)
            ) {
              const value =
                subround.securityType?.value === 3
                  ? parseFloat(subround.conversionAmount)
                  : parseFloat(subround.dealSize);

              const acquiredCalc =
                value /
                (value +
                  Math.min(
                    parseFloat(subround.cap),
                    parseFloat(nextSubround.preMoneyValuation) *
                      (1 - parseFloat(subround.discount) / 100),
                  ));

              const safeAcquiredCalc = Number.isFinite(acquiredCalc)
                ? acquiredCalc.toString()
                : '0';

              if (safeAcquiredCalc !== subround.acquiredCalc) {
                formik.setFieldValue(
                  `form.rounds.${roundIndex}.subrounds.${index}.acquiredCalc`,
                  safeAcquiredCalc,
                );
              }
            } else if (subround.acquiredCalc !== '') {
              formik.setFieldValue(
                `form.rounds.${roundIndex}.subrounds.${index}.acquiredCalc`,
                '',
                false,
              );
            }

            if (subround.securityType?.value === 3) {
              if (
                subround.dealSize &&
                subround.interest &&
                subround.dealDate &&
                (nextSubround?.dealDate || lastSubround)
              ) {
                const outstandingDays = lastSubround
                  ? 0
                  : differenceInDays(
                      startOfDay(nextSubround.dealDate as Date),
                      startOfDay(subround.dealDate),
                    );

                const conversionAmount =
                  parseFloat(subround.dealSize) *
                  (1 + parseFloat(subround.interest) / 100) ** (outstandingDays / 365);

                const safeConversionAmount = Number.isFinite(conversionAmount)
                  ? conversionAmount.toString()
                  : '0';

                if (safeConversionAmount !== subround.conversionAmount) {
                  formik.setFieldValue(
                    `form.rounds.${roundIndex}.subrounds.${index}.conversionAmount`,
                    safeConversionAmount,
                  );
                }
              } else if (subround.conversionAmount !== '') {
                formik.setFieldValue(
                  `form.rounds.${roundIndex}.subrounds.${index}.conversionAmount`,
                  '',
                  false,
                );
              }
            }
          }
        });
      }
    }, [formik, roundIndex]);

    const handleActiveTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
      event.stopPropagation();

      setActiveTabIndex(newValue);
    }, []);

    const toggleExpanded = useCallback(() => {
      setExpanded(prevValue => !prevValue);
    }, []);

    const handleNewSubroundClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
      event => {
        event.stopPropagation();

        if (activeTabIndex === round.subrounds.length) {
          setActiveTabIndex(activeTabIndex + 1);
        }

        formik.setFieldValue(
          `form.rounds.${roundIndex}.subrounds`,
          [
            ...round.subrounds,
            {
              ...subroundInitialValue,
              id: uuidv4(),
              securityType:
                roundIndex === 2 && securityTypesQuery.data
                  ? {
                      value: securityTypesQuery.data.result.data[0].id,
                      label: securityTypesQuery.data.result.data[0].name,
                    }
                  : null,
            },
          ],
          false,
        );
      },
      [activeTabIndex, formik, round.subrounds, roundIndex, securityTypesQuery.data],
    );

    const handleSubroundRemove = useCallback(
      (event: React.MouseEvent<HTMLDivElement, MouseEvent>, id: string) => {
        event.stopPropagation();

        if (activeTabIndex === round.subrounds.length) {
          setActiveTabIndex(activeTabIndex - 1);
        } else if (activeTabIndex === round.subrounds.length - 1) {
          setActiveTabIndex(round.subrounds.length - 2);
        }

        formik.setFieldValue(
          `form.rounds.${roundIndex}.subrounds`,
          round.subrounds.filter(subround => subround.id !== id),
          false,
        );
      },
      [activeTabIndex, formik, round.subrounds, roundIndex],
    );

    const handleSubroundsSort = useCallback(
      async (
        roundIndex: number,
        dragIndex: number,
        dragData: SubroundData,
        hoverIndex: number,
        hoverData: SubroundData,
      ) => {
        await formik.setFieldValue(`form.rounds.${roundIndex}.subrounds.${dragIndex}`, hoverData);
        await formik.setFieldValue(`form.rounds.${roundIndex}.subrounds.${hoverIndex}`, dragData);

        await sleep();

        await formik.setTouched(setNestedObjectValues(formik.values, true));

        setActiveTabIndex(hoverIndex);
      },
      [formik],
    );

    const handleSubroundsSwap = useCallback(
      async (
        dragRoundIndex: number,
        dragIndex: number,
        dragData: SubroundData,
        dropRoundIndex: number,
        dropIndex: number,
        dropData: SubroundData,
      ) => {
        await formik.setFieldValue(
          `form.rounds.${dragRoundIndex}.subrounds.${dragIndex}`,
          dropData,
        );
        await formik.setFieldValue(
          `form.rounds.${dropRoundIndex}.subrounds.${dropIndex}`,
          dragData,
        );

        await formik.setTouched(setNestedObjectValues(formik.values, true));
      },
      [formik],
    );

    if (swapping) {
      drag(drop(titleRef));
    }

    return (
      <div className={clsx(styles.round, className)}>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
        <div className={styles.round__heading} role="button" tabIndex={-1} onClick={toggleExpanded}>
          <div className={styles['round__heading__first-column']}>
            <div
              className={clsx(styles.round__name, { [styles['round__name--swap']]: swapping })}
              data-handler-id={handlerId}
              ref={titleRef}
              style={isDragging ? { opacity: 0 } : undefined}
              title={swapping ? 'Move' : undefined}
            >
              {swapping && (
                <DragIndicatorIcon
                  sx={{
                    width: '16px',
                    height: '16px',
                    color: 'var(--gray-6-color)',
                    mr: '4px',
                  }}
                />
              )}
              <Typography color="text.primary" fontWeight="700" variant="subtitle1">
                Round {round.name}
              </Typography>
            </div>
            <Tabs
              indicatorColor="primary"
              scrollButtons="auto"
              value={activeTabIndex === null ? false : activeTabIndex}
              variant="scrollable"
              onChange={handleActiveTabChange}
            >
              {round.subrounds.map((subround, index) => (
                <DraggableTab
                  companyLocked={companyLocked}
                  index={index}
                  key={subround.id}
                  roundIndex={roundIndex}
                  roundName={round.name}
                  subroundData={subround}
                  subroundsLength={round.subrounds.length}
                  swapping={swapping}
                  onRemoveClick={handleSubroundRemove}
                  onSubroundsSort={handleSubroundsSort}
                  onSubroundsSwap={handleSubroundsSwap}
                />
              ))}
              <Tab label="Summary" />
            </Tabs>
          </div>
          <div className={styles['round__heading__second-column']}>
            <Button
              color="secondary"
              disabled={companyLocked}
              size="small"
              startIcon={<AddIcon sx={{ width: '24px', height: '24px' }} />}
              sx={{ width: '144px', pl: '8px', pr: '8px', fontSize: '14px', mr: '6px' }}
              onClick={handleNewSubroundClick}
            >
              New Subround
            </Button>
            {expanded ? (
              <ArrowDropUpIcon sx={{ color: 'var(--gray-2-color)' }} />
            ) : (
              <ArrowDropDownIcon sx={{ color: 'var(--gray-2-color)' }} />
            )}
          </div>
        </div>
        {expanded && (
          <>
            {activeTabIndex < round.subrounds.length && (
              <Subround
                companyLocked={companyLocked}
                key={round.subrounds[activeTabIndex].id}
                previousRoundSubroundsCount={previousRound?.subrounds.length || 0}
                roundIndex={roundIndex}
                subroundIndex={activeTabIndex}
              />
            )}
            {activeTabIndex === round.subrounds.length && <Summary roundIndex={roundIndex} />}
          </>
        )}
      </div>
    );
  },
);

export const Round = memo(Component);
